<script setup lang="ts">
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';

export type StepperStep = {
    title: string;
    icon: IconDefinition;
    clickDisabled?: boolean;
};

type StepperStepProp = {
    title: string;
    icon: {
        icon: IconDefinition;
        color: string;
    };
    clickDisabled?: boolean;
};
interface IProps {
    steps?: StepperStep[];
    disabled?: boolean;
}
const { steps = [], disabled } = defineProps<IProps>();

const modelValue = defineModel<number>({ default: 0 });

const actualSteps = computed<StepperStepProp[]>(() =>
    steps?.map((step, index) => ({
        ...step,
        clickDisabled: step.clickDisabled || disabled,
        icon: {
            icon: modelValue.value > index ? faCheck : step.icon,
            color:
                modelValue.value > index
                    ? 'var(--blue-400)'
                    : modelValue.value === index
                      ? 'white'
                      : 'var(--blue-100)',
        },
    }))
);
function click(index: number) {
    if (actualSteps.value[index].clickDisabled) {
        return;
    }
    if (modelValue.value <= index) {
        return;
    }
    modelValue.value = index;
}
</script>
<template>
    <div class="mr-stepper-root-container">
        <div class="mr-stepper-root">
            <template
                v-for="(step, index) in actualSteps"
                :key="`content-${step.title}`"
            >
                <div
                    class="step-content"
                    :class="{
                        clickable: !step.clickDisabled && index < modelValue,
                        disabled: step.clickDisabled && index < modelValue,
                    }"
                    @click.stop="click(index)"
                >
                    <div
                        class="icon-wrapper"
                        :class="{
                            current: index === modelValue,
                            next: index > modelValue,
                            prev: index < modelValue,
                        }"
                    >
                        <mr-icon
                            :icon="step.icon.icon"
                            :color="step.icon.color"
                            size="small"
                        />
                    </div>
                    <span
                        class="title"
                        :class="{
                            current: index === modelValue,
                            next: index > modelValue,
                            prev: index < modelValue,
                        }"
                    >
                        {{ step.title }}
                    </span>
                </div>
                <mr-divider
                    v-if="index !== steps.length - 1"
                    :key="`divider-${step.title}`"
                    :color="modelValue > index ? 'white' : 'var(--blue-300)'"
                    :border-style="modelValue > index ? 'solid' : 'dashed'"
                    class="step-divider"
                    :style="{
                        opacity: modelValue > index ? 0.5 : 1,
                    }"
                />
            </template>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.mr-stepper-root-container {
    width: 100%;
    padding: 4px 12px 32px;
    display: flex;
    .mr-stepper-root {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-grow: 1;

        .step-content {
            width: 60px;
            height: 36px;
            display: flex;
            flex-direction: column;
            gap: 4px;
            align-items: center;
            flex-shrink: 0;
            &.clickable {
                cursor: pointer;
            }
            &.disabled {
                cursor: not-allowed;
            }
            .icon-wrapper {
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 50%;
                flex-shrink: 0;

                &.prev {
                    background-color: white;
                    width: 36px;
                    height: 36px;
                }

                &.current {
                    border: 2px solid white;
                    width: 40px;
                    height: 40px;
                    margin-top: -2px;
                }

                &.next {
                    background-color: usePalette($p-blue, $p-300);
                    width: 36px;
                    height: 36px;
                }
            }

            .title {
                white-space: nowrap;

                &.prev {
                    @include tg-s($color: white);
                    text-align: center;
                }

                &.current {
                    @include tg-m($w7, white);
                    text-align: center;
                }

                &.next {
                    @include tg-s($color: usePalette($p-blue, $p-100));
                    text-align: center;
                }
            }
        }

        .step-divider {
            margin: 20px;
            align-self: stretch;
        }
    }
}
</style>
